import { useAsync } from '@shared/async';
import { useMarkdown } from '@shared/markdown/use-markdown';
import { PaTypeCell } from '@shared/pa-info/pa-type-cell';
import { isValidDate } from '@shared/validators/date-time-validators';
import {
  Box,
  Button,
  CheckBox,
  FormField,
  MaskedInput,
  RadioButtonGroup,
  Select,
  SelectMultiple,
  Text,
  TextInput,
} from 'grommet';
import { Close, Edit } from 'grommet-icons';
import { isNil } from 'lodash';
import React, { FormEventHandler, KeyboardEvent, ReactNode, useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { Date } from '@/components/date-time';
import {
  BorderlessFormField,
  DateTextInput,
  InMemorySelectList,
  LazyLoadMultiSelectList,
  LazyLoadSelectList
} from '@/components/form-controls';
import { Field } from '@/components-new/fieldset';
import { RichTextEditorInput, RichTextPreview } from '@/components-new/rich-text-editor';
import { changeLogReasonOptions } from '@/features/changelog/types';
import {
  EditCoverageRequest,
  parseStepTherapyCount
} from '@/features/coverage/api/use-coverage-service';
import { PdlStatusCell } from '@/features/coverage/components/index/pdl-status-cell';
import { PdlStatus, pdlStatusOptions } from '@/features/coverage/types/pdl-status';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { Lookup } from '@/types/lookup';
import { State } from '@/types/state';

type FieldName = keyof EditCoverageRequest;

type ClinicalsProductEditFormProps = {

  /**
   * ID of the `form` element, required. Necessary to associate a submit button with the form.
   */
  formId: string;

  /**
   * Submit handler for the form, required. Usually wrapped by React Hook Form's `handleSubmit`.
   */
  onSubmit: FormEventHandler<HTMLDivElement>;

  /**
   * Indicates what the form is being used for, defaults to 'single-edit'. Primarily, this indicates which fields
   * should be shown to the user.
   *
   * The options are:
   * - 'bulk-edit': hides "Notes", "State", and "Drug" fields, since those don't make sense to allow bulk edits for
   * - 'single-edit': hides "State" and "Drug" fields, since those are only needed for initial creation
   * - 'add': shows all fields
   */
  mode?: 'bulk-edit' | 'single-edit' | 'add';

  /**
   * Dictates the content to show when a field isn't editable, optional.
   * The valid values are:
   * - show-default-value: shows the default value for the field
   * - show-no-change: shows the text "No Change"
   */
  fieldDisplayMode?: 'show-default-value' | 'show-no-change';

  /**
   * The fields that are editable by the user, optional. If not provided, all fields will be shown as editable.
   */
  visibleFields?: FieldName[];

  /**
   * Callback for when the user selects a field to edit.
   * @param fieldName the name of the field
   */
  onFieldShow?: (fieldName: FieldName) => void;

  /**
   * Callback for when the user hides a field
   * @param fieldName the name of the field
   */
  onFieldHide?: (fieldName: FieldName) => void;

  /**
   * Any content to display at the top of the form, optional.
   */
  children?: ReactNode;

  /**
   * The IDs of the drugs that are filtered out of the drug selection list, optional.
   */
  filteredDrugIds?: number[] | null;

  /**
   * The selected state, optional.
   */
  selectedState?: State | null;
};


/**
 * Represents a form for editing clinicals product.
 */
export const ClinicalsProductEditForm = (props: ClinicalsProductEditFormProps) => {
  const {
    formId,
    onSubmit,
    mode = 'single-edit',
    fieldDisplayMode = 'show-no-change',
    visibleFields,
    onFieldShow,
    onFieldHide,
    children,
    filteredDrugIds,
    selectedState
  } = props;
  const form = useFormContext<EditCoverageRequest>();

  const { toHtml, fromHtml } = useMarkdown();

  const { getPaTypeLookups, getStates } = useLookupsService();

  const paTypesAsync = useAsync(getPaTypeLookups);
  const statesAsync = useAsync(getStates);

  useEffect(() => {
    void paTypesAsync.execute();
    void statesAsync.execute();
    // TODO: update this to useQuery so we no longer need this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      id={formId}
      as="form"
      fill="horizontal"
      onSubmit={onSubmit}
      gap="medium"
    >
      {children}
      {/* use the default for Change Reason on creation */}
      {mode !== 'add' ? (
        <Controller
          name="changeReason"
          control={form.control}
          render={({ field: { name, onChange, onBlur, value } }) => (
            <Box>
              <Text weight="bold">Reason for Change</Text>
              <BorderlessFormField
                name={name}
                htmlFor={`${formId}-${name}`}
              >
                <RadioButtonGroup
                  name={name}
                  id={`${formId}-${name}`}
                  value={value}
                  options={changeLogReasonOptions.map((reason) => ({ ...reason, value: reason.id }))}
                  onChange={onChange}
                  onBlur={onBlur}
                  direction="row"
                />
              </BorderlessFormField>
            </Box>
          )}
        />
      ) : null}
      <FieldsContainer>
        <AddProductFields 
          mode={mode}
          formId={formId}
          visibleFields={visibleFields}
          onFieldShow={onFieldShow}
          onFieldHide={onFieldHide}
          fieldDisplayMode={fieldDisplayMode}
          filteredDrugIds={filteredDrugIds}
          statesAsync={statesAsync}
        />
        <Controller
          name="pdlStatusDate"
          control={form.control}
          rules={{
            // @ts-expect-error TS(2322): Type '(value: string) => string | undefined' is no... Remove this comment to see the full error message
            validate: isValidDate
          }}
          render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => (
            <VerticalFormField
              htmlFor={`${formId}-${name}`}
              label="PDL Status Date"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              nonEditableText={fieldDisplayMode === 'show-default-value' ? <Date value={value}/> : 'No Change'}
            >
              {!visibleFields || visibleFields.includes(name) ? (
                <DateTextInput
                  name={name}
                  id={`${formId}-${name}`}
                  value={value}
                  onChange={(event) => onChange((event.target as any).value)}
                  onBlur={onBlur}
                />
              ) : (
                <div>No Change</div>
              )}
            </VerticalFormField>
          )}
        />
        <Controller
          name="pdlStatusEffectiveDate"
          control={form.control}
          rules={{
            // @ts-expect-error TS(2322): Type '(value: string) => string | undefined' is no... Remove this comment to see the full error message
            validate: isValidDate
          }}
          render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => (
            <VerticalFormField
              htmlFor={`${formId}-${name}`}
              label="Status Change Effective Date"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              nonEditableText={fieldDisplayMode === 'show-default-value' ? <Date value={value}/> : 'No Change'}
            >
              <DateTextInput
                name={name}
                id={`${formId}-pdlStatusEffectiveDate`}
                value={value}
                onChange={(event) => onChange((event.target as any).value)}
                onBlur={onBlur}
              />
            </VerticalFormField>
          )}
        />
        <Controller
          name="pdlStatus"
          control={form.control}
          rules={{
            validate: (value) => {
              if (value === PdlStatus.BLANK) {
                return 'Please select a valid PDL Status';
              }
              return true;
            }
          }}
          render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => (
            <VerticalFormField
              htmlFor={`${formId}-${name}`}
              label="PDL Status"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              nonEditableText={fieldDisplayMode === 'show-default-value' ? <PdlStatusCell pdlStatus={value}/> : 'No Change'}
            >
              <Select
                name={name}
                id={`${formId}-${name}`}
                placeholder="Select a PDL Status"
                options={pdlStatusOptions}
                // @ts-expect-error TS(2322): Type '{ id: PdlStatus; label: string | null; } | n... Remove this comment to see the full error message
                value={pdlStatusOptions.find(x => x.id === value) ?? null}
                disabledKey="disabled"
                labelKey="label"
                valueKey="id"
                onBlur={onBlur}
                onChange={({ value: nextValue }) => onChange(nextValue ? nextValue.id : null)}
                ref={ref}
                clear={{ label: 'Clear Selection' }}
              />
            </VerticalFormField>
          )}
        />          <PaTypesField
            formId={formId}
            form={form}
            visibleFields={visibleFields}
            onFieldShow={onFieldShow}
            onFieldHide={onFieldHide}
            fieldDisplayMode={fieldDisplayMode}
            selectedState={selectedState}
            paTypes={paTypesAsync.value ?? []}
            statesAsync={statesAsync}
          />
        <Controller
          name="stepTherapyCount"
          control={form.control}
          rules={{
            validate: (value) => {
              if (!value) {
                return true;
              }
              const result = parseStepTherapyCount(value);
              if (typeof result === 'string') {
                return result;
              }
              return true;
            }
          }}
          render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => (
            <VerticalFormField
              htmlFor={`${formId}-${name}`}
              label="Step Therapy"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              nonEditableText={fieldDisplayMode === 'show-default-value' ? value : 'No Change'}
            >
              <MaskedInput
                name={name}
                id={`${formId}-${name}`}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                mask={[{ regexp: /^[1-9]?[0-9]\+?$/ }]}
              />
            </VerticalFormField>
          )}
        />
        <Controller
          name="paCriteriaSourceUrl"
          control={form.control}
          rules={{
            validate: (value) => {
              // empty value is valid
              if (!value) {
                return true;
              }
              try {
                new URL(value);
                return true;
              } catch {
                return 'Must be a valid URL';
              }
            }
          }}
          render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => (
            <VerticalFormField
              htmlFor={`${formId}-${name}`}
              label="PA Criteria Link"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              nonEditableText={fieldDisplayMode === 'show-default-value'
                ? value
                  ? value
                  : null
                : 'No Change'
              }
            >
              <TextInput
                name={name}
                id={`${formId}-${name}`}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
              />
            </VerticalFormField>
          )}
        />
        <Controller
          name="hasAutoPa"
          control={form.control}
          render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => (
            <VerticalFormField
              alreadyHasFormField
              htmlFor={`${formId}-${name}`}
              label="Has Auto PA?"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              nonEditableText={fieldDisplayMode === 'show-default-value' ? (value ? 'Yes' : 'No') : 'No Change'}
            >
              {!visibleFields || visibleFields.includes(name) ? (
                <CheckBox
                  name={name}
                  id={`${formId}-${name}`}
                  checked={value}
                  onChange={(event) => onChange(event.target.checked)}
                  onBlur={onBlur}
                />
              ) : (
                <div>No Change</div>
              )}
            </VerticalFormField>
          )}
        />
        
        {mode !== 'bulk-edit' && (
          <StatePaCriteriaLinkField 
            formId={formId} 
            form={form}
            visibleFields={visibleFields}
            onFieldShow={onFieldShow}
            onFieldHide={onFieldHide}
            fieldDisplayMode={fieldDisplayMode}
            selectedState={selectedState}
            statesAsync={statesAsync}
          />
        )}

        <Controller
          name="paCriteriaDescription"
          control={form.control}
          render={({ field: { value, onChange, name, ref }, fieldState: { error } }) => (
            <VerticalFormField
              alreadyHasFormField
              htmlFor={`${formId}-${name}`}
              label="PA Criteria Text"
              actions={visibleFields && onFieldShow && onFieldHide ? (
                <FieldActions
                  name={name}
                  visibleFields={visibleFields}
                  onFieldShow={onFieldShow}
                  onFieldHide={onFieldHide}
                />
              ) : null}
              name={name}
              visibleFields={visibleFields}
              error={error?.message}
              nonEditableText={fieldDisplayMode === 'show-default-value'
                ? value
                  ? <RichTextPreview content={toHtml(value)}/>
                  : null
                : 'No Change'}
            >
              <Field>
                <RichTextEditorInput
                  className="max-h-96"
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') {
                      event.stopPropagation();
                    }
                  }}
                  ref={ref}
                  value={toHtml(value ?? '')}
                  onChange={(html) => onChange(fromHtml(html))}
                  placeholder="Fill out PA Criteria..."
                />
              </Field>
            </VerticalFormField>
          )}
        />
        {/* Can't edit Notes in bulk edit mode */}
        {mode !== 'bulk-edit' ? (
          <Controller
            name="notes"
            control={form.control}
            render={({ field: { value, onChange, name, ref }, fieldState: { error } }) => (
              <VerticalFormField
                alreadyHasFormField
                htmlFor={`${formId}-${name}`}
                label="Notes"
                actions={visibleFields && onFieldShow && onFieldHide ? (
                  <FieldActions
                    name={name}
                    visibleFields={visibleFields}
                    onFieldShow={onFieldShow}
                    onFieldHide={onFieldHide}
                  />
                ) : null}
                name={name}
                visibleFields={visibleFields}
                error={error?.message}
                nonEditableText={fieldDisplayMode === 'show-default-value'
                  ? value
                    ? <RichTextPreview content={toHtml(value)}/>
                    : null
                  : 'No Change'}
              >
                <Field>
                  <RichTextEditorInput
                    className="max-h-96"
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === 'Enter') {
                        event.stopPropagation();
                      }
                    }}
                    ref={ref}
                    value={toHtml(value ?? '')}
                    onChange={(html) => onChange(fromHtml(html))}
                    placeholder="Additional change notes..."
                  />
                </Field>
              </VerticalFormField>
            )}
          />
        ) : null}
      </FieldsContainer>
    </Box>
  );
};

type AddProductFieldsProps = {
  mode: ClinicalsProductEditFormProps['mode'];
  formId: string;
  visibleFields?: FieldName[];
  onFieldShow?: (fieldName: FieldName) => void;
  onFieldHide?: (fieldName: FieldName) => void;
  fieldDisplayMode?: 'show-default-value' | 'show-no-change';
  filteredDrugIds?: number[] | null;
  statesAsync: any;
};

const AddProductFields = ({ mode, formId, visibleFields, onFieldShow, onFieldHide, fieldDisplayMode, filteredDrugIds, statesAsync }:  AddProductFieldsProps) => {
  const form = useFormContext<EditCoverageRequest>();
  const { setValue } = form;
  const { getProductLookups, getCoverageTags } = useLookupsService();
  const drugSelection = useWatch({ control: form.control, name: 'drug' });

  const previousDrugSelectionRef = useRef<Lookup | undefined>();

  // set the filtered drug id only if there is one drug id and the mode is not 'add'
  // this is to prevent the drug selection from being set to a single drug id when in 'add' mode
  // this is because in 'add' mode, the user should be able to select any drug
  const filteredDrugId = filteredDrugIds?.length == 1 && mode != 'add' ? filteredDrugIds[0] : undefined;

  useEffect(() => {
    if (previousDrugSelectionRef.current?.id !== drugSelection?.id && mode === 'add') {
      setValue('coverageTags', []);
    }
    previousDrugSelectionRef.current = drugSelection;
  }, [drugSelection, setValue, mode]);

  const stateOptions = useMemo(() => {
    if (!statesAsync.value) return [];

    return statesAsync.value.map((it: any) => ({ id: it.id, label: it.name }));
  }, [statesAsync.value]);

  const drugField = (
    <Controller
      name="drug"
      control={form.control}
      rules={{
        required: !visibleFields || visibleFields.includes('drug') ? 'Drug is a required field' : false
      }}
      render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => (
        <VerticalFormField
          htmlFor={`${formId}-${name}`}
          label="Drug"
          actions={visibleFields && onFieldShow && onFieldHide ? (
            <FieldActions
              name={name}
              visibleFields={visibleFields}
              onFieldShow={onFieldShow}
              onFieldHide={onFieldHide}
            />
          ) : null}
          name={name}
          visibleFields={visibleFields}
          error={error?.message}
          nonEditableText={fieldDisplayMode === 'show-default-value' ? value?.label : 'No Change'}
          alreadyHasFormField
        >
          {!visibleFields || visibleFields.includes(name) ? (
            <LazyLoadSelectList
              value={value}
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Select a Drug"
              lazyLoadRequest={(searchTerm, page, rpp) => {
                return getProductLookups({ query: searchTerm, page, rpp });
              }}
            />
          ) : (
            <div>No Change</div>
          )}
        </VerticalFormField>
      )}
    />
  );
  
  const additionalTagsField = (
    <Controller
      name="coverageTags"
      control={form.control}
      rules={{
        required: false
      }}
      render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => (
        <VerticalFormField
          htmlFor={`${formId}-${name}`}
          label="Coverage Tags"
          actions={visibleFields && onFieldShow && onFieldHide ? (
            <FieldActions
              name={name}
              visibleFields={visibleFields}
              onFieldShow={onFieldShow}
              onFieldHide={onFieldHide}
            />
          ) : null}
          help={isNil(drugSelection) && !filteredDrugId ? 'You must select a drug before adding tags.' : undefined}
          name={name}
          visibleFields={visibleFields}
          error={error?.message}
          nonEditableText={fieldDisplayMode === 'show-default-value' ? value?.map((lookup: Lookup) => lookup.label).join(', ') : 'No Change'}
          alreadyHasFormField
        >
          {!visibleFields || visibleFields.includes(name) ? (
              <LazyLoadMultiSelectList
                key={previousDrugSelectionRef?.current?.id ?? filteredDrugId}
                disabled={isNil(drugSelection?.id) && !filteredDrugId}
                showSelectedInline
                placeholder="Select Coverage Tags"
                value={value}
                error={error?.message}
                onChange={onChange}
                onBlur={onBlur}
                lazyLoadRequest={async (searchTerm, page, rpp) => {
                  const parameters = {
                    query: searchTerm,
                    page,
                    rpp,
                    drugId: drugSelection?.id ?? filteredDrugId
                  };

                  const result = await getCoverageTags(parameters);
                  result.items = result.items
                    .filter(tag => tag.nested)
                    .map(tag => {
                        return {
                          ...tag,
                          nested: false,
                          label: tag.label.replace(drugSelection?.label || '', '').trim()
                        };
                    });

                  return result;
                }}
              />
          ) : (
            <div>No Change</div>
          )}
        </VerticalFormField>
      )}
    />
  );


  switch (mode) {
    case 'add':
      return (
        <>
          {drugField}
          {additionalTagsField}
          <StateField
            formId={formId}
            form={form}
            visibleFields={visibleFields}
            onFieldShow={onFieldShow}
            onFieldHide={onFieldHide}
            fieldDisplayMode={fieldDisplayMode}
            stateOptions={stateOptions}
            statesAsync={statesAsync}
          />
        </>
      );
    case 'single-edit':
      return (
        <>
          {additionalTagsField}
        </>
      );
    case 'bulk-edit':
      return (
        <>
          {filteredDrugId ? additionalTagsField : null}
        </>
      );
    default:
      return null;
  }
};

type FieldActionsProps = {
  name: FieldName;
  visibleFields: FieldName[];
  onFieldShow: (field: FieldName) => void;
  onFieldHide: (field: FieldName) => void;
}

const FieldActions = ({ name, visibleFields, onFieldShow, onFieldHide }: FieldActionsProps) => {
  if (visibleFields.includes(name)) {
    return (
      <Button
        hoverIndicator
        icon={<Close/>}
        a11yTitle="Cancel"
        tip="Cancel"
        onClick={() => onFieldHide(name)}
      />
    );
  }

  return (
    <Button
      hoverIndicator
      icon={<Edit/>}
      a11yTitle="Edit"
      tip="Edit"
      onClick={() => onFieldShow(name)}
    />
  );
};

const FieldsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: max-content;
    grid-auto-flow: row;
    align-items: center;
    row-gap: 1.5rem;
    column-gap: 0.5rem;
`;

type VerticalFormFieldProps = {
  htmlFor: string;
  label: string;
  help?: string;
  actions?: ReactNode;
  children: ReactNode;
  error?: string;
  name: FieldName;
  visibleFields?: FieldName[];
  nonEditableText: ReactNode;
  alreadyHasFormField?: boolean;
};

const VerticalFormField = ({ htmlFor, label, actions, children, error, name, help, visibleFields, nonEditableText, alreadyHasFormField }: VerticalFormFieldProps) => {
  const content = alreadyHasFormField
    ? <>{children}</>
    : <FormField margin="none">{children}</FormField>;

  return (
    <>
      <LabelContainer>
        <label htmlFor={htmlFor}>
          <Text weight="bold">{label}</Text>
        </label>
        {actions}
      </LabelContainer>
      <FieldContainer>
        {!visibleFields || visibleFields.includes(name) ? content : (
          <Text>{nonEditableText ?? <>&mdash;</>}</Text>
        )}
        {error && <Text color="error" size="small">{error ? error : null}</Text>}
        {!error && (<Text color="placeholder" size="small">{help ? help : null}</Text>)}
      </FieldContainer>
    </>
  );
};

const LabelContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
`;

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    justify-content: center;
`;

// Helper component to handle the isStatePaCriteriaSameAsPdlLink field
const StatePaCriteriaLinkField = ({ 
  formId, 
  form, 
  visibleFields, 
  onFieldShow, 
  onFieldHide, 
  fieldDisplayMode, 
  selectedState,
  statesAsync 
}: { 
  formId: string; 
  form: any;
  visibleFields?: FieldName[];
  onFieldShow?: (fieldName: FieldName) => void;
  onFieldHide?: (fieldName: FieldName) => void;
  fieldDisplayMode?: 'show-default-value' | 'show-no-change';
  selectedState?: State | null;
  statesAsync: any;
}) => {
  // Field name constant
  const fieldName = 'isStatePaCriteriaSameAsPdlLink';

  // Get the current state selection
  const stateSelection = useWatch({ control: form.control, name: 'state' });
  const isStateSelected = Boolean(stateSelection?.id ?? selectedState?.id);
  
  // Access states data directly from the statesAsync prop
  const statesData = statesAsync.value;

  // Inline implementation of handleIsStatePaCriteriaSameAsPdlLinkChange
  const handleIsStatePaCriteriaSameAsPdlLinkChange = (isChecked: boolean) => {
    // Get current state information
    const stateData = form.getValues('state');
    const stateId = stateData?.id ?? selectedState?.id;
    
    if (stateId && statesData) {
      if (isChecked) {
        // If checked and we have a state selected, update the PA criteria URL
        const selectedStateObj = statesData.find((state: { id: any; }) => state.id === stateId);
        if (selectedStateObj?.pdlLink) {
          form.setValue('paCriteriaSourceUrl', selectedStateObj.pdlLink, { shouldValidate: true, shouldDirty: true });
        }
      } else {
        // If unchecked, clear the PA criteria URL
        form.setValue('paCriteriaSourceUrl', '', { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  // Watch for changes in the state selection and update URL accordingly
  const stateId = stateSelection?.id;
  
  // Effect for when the state changes but the checkbox is already checked
  useEffect(() => {
    const currentValue = form.getValues(fieldName);
    if (currentValue && isStateSelected && statesData) {
      handleIsStatePaCriteriaSameAsPdlLinkChange(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, isStateSelected, statesData]);
  
  return (
    <Controller
      name={fieldName}
      control={form.control}
      render={({ field: { value, onChange, onBlur, name }, fieldState: { error } }) => {
        // Handle checkbox change with inline controller logic
        const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const isChecked = event.target.checked;
          onChange(isChecked);
          handleIsStatePaCriteriaSameAsPdlLinkChange(isChecked);
        };

        return (
          <VerticalFormField
            alreadyHasFormField
            htmlFor={`${formId}-${name}`}
            label="State PA Criteria Link same as State PDL Link?"
            actions={visibleFields && onFieldShow && onFieldHide ? (
              <FieldActions
                name={name}
                visibleFields={visibleFields}
                onFieldShow={onFieldShow}
                onFieldHide={onFieldHide}
              />
            ) : null}
            name={name}
            visibleFields={visibleFields}
            error={error?.message}
            help={!isStateSelected ? 'Select a state to enable this option' : undefined}
            nonEditableText={fieldDisplayMode === 'show-default-value' ? (value ? 'Yes' : 'No') : 'No Change'}
          >
            {!visibleFields || visibleFields.includes(name) ? (
              <CheckBox
                name={name}
                id={`${formId}-${name}`}
                checked={value}
                onChange={handleCheckboxChange}
                onBlur={onBlur}
                disabled={!isStateSelected}
              />
            ) : (
              <div>No Change</div>
            )}
          </VerticalFormField>
        );
      }}
    />
  );
};

// Helper component to handle the PA Types field
const PaTypesField = ({ 
  formId, 
  form, 
  visibleFields, 
  onFieldShow, 
  onFieldHide, 
  fieldDisplayMode, 
  selectedState,
  paTypes,
  statesAsync
}: { 
  formId: string; 
  form: any;
  visibleFields?: FieldName[];
  onFieldShow?: (fieldName: FieldName) => void;
  onFieldHide?: (fieldName: FieldName) => void;
  fieldDisplayMode?: 'show-default-value' | 'show-no-change';
  selectedState?: State | null;
  paTypes: Lookup[];
  statesAsync: any;
}) => {
  return (
    <Controller
      name="paTypes"
      control={form.control}
      render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => {
        // Handle PA Types changes with inline controller logic
        const handlePaTypesChange = ({ value: nextValue }: { value: Lookup[] }) => {
          onChange(nextValue);
          
          // Get states data directly from props
          const statesData = statesAsync.value;
          
          // Inline implementation of handlePaTypesChange logic
          if (statesData) {
            // Get current state information
            const stateData = form.getValues('state');
            const stateId = stateData?.id ?? selectedState?.id;
            
            if (stateId) {
              const currentState = statesData.find((state: { id: any; }) => state.id === stateId);
              
              if (currentState) {
                // Check if General PDL or General Non-PDL is selected
                const hasGeneralPdl = nextValue.some(type => type.id === 1); // General PDL
                const hasGeneralNonPdl = nextValue.some(type => type.id === 2); // General Non-PDL
                
                // Get current PA criteria text
                let currentText = form.getValues('paCriteriaDescription') || '';
                let shouldUpdateText = false;
                
                // Handle General PDL text
                if (hasGeneralPdl && currentState.generalPaCriteriaPdlReviewed) {
                  // Add PDL text if it doesn't exist
                  const pdlText = currentState.generalPaCriteriaPdlReviewed;
                  if (!currentText.includes(pdlText)) {
                    currentText = currentText ? `${currentText}\n\n${pdlText}` : pdlText;
                    shouldUpdateText = true;
                  }
                } else if (!hasGeneralPdl && currentState.generalPaCriteriaPdlReviewed) {
                  // Remove PDL text if PA type has been unselected
                  const pdlText = currentState.generalPaCriteriaPdlReviewed;
                  if (currentText.includes(pdlText)) {
                    // Remove the text and clean up extra newlines
                    currentText = currentText.replace(pdlText, '').replace(/\n\n\n+/g, '\n\n').trim();
                    shouldUpdateText = true;
                  }
                }
                
                // Handle General Non-PDL text
                if (hasGeneralNonPdl && currentState.generalPaCriteriaPdlNonReviewed) {
                  // Add Non-PDL text if it doesn't exist
                  const nonPdlText = currentState.generalPaCriteriaPdlNonReviewed;
                  if (!currentText.includes(nonPdlText)) {
                    currentText = currentText ? `${currentText}\n\n${nonPdlText}` : nonPdlText;
                    shouldUpdateText = true;
                  }
                } else if (!hasGeneralNonPdl && currentState.generalPaCriteriaPdlNonReviewed) {
                  // Remove Non-PDL text if PA type has been unselected
                  const nonPdlText = currentState.generalPaCriteriaPdlNonReviewed;
                  if (currentText.includes(nonPdlText)) {
                    // Remove the text and clean up extra newlines
                    currentText = currentText.replace(nonPdlText, '').replace(/\n\n\n+/g, '\n\n').trim();
                    shouldUpdateText = true;
                  }
                }
                
                // Handle PA forms link updates
                if (hasGeneralPdl || hasGeneralNonPdl) {
                  // Add PA forms link when either type is selected
                  if (currentState.paFormsLink) {
                    form.setValue('paCriteriaSourceUrl', currentState.paFormsLink,{ shouldValidate: true, shouldDirty: true });
                  }
                } else {
                  // Clear PA forms link when neither type is selected
                  // Check if the current URL matches the state's paFormsLink to avoid clearing custom URLs
                  const currentUrl = form.getValues('paCriteriaSourceUrl');
                  if (currentUrl === currentState.paFormsLink) {
                    form.setValue('paCriteriaSourceUrl', '', { shouldValidate: true, shouldDirty: true });
                  }
                }
                
                // Only update if text has changed
                if (shouldUpdateText) {
                  form.setValue('paCriteriaDescription', currentText, { shouldValidate: true, shouldDirty: true });
                }
                
                // Update Step Therapy Count based on PA Types
                if (hasGeneralPdl && !hasGeneralNonPdl) {
                  // Set step therapy value for General PDL only
                  if (currentState.generalPaStepTherapy) {
                    form.setValue('stepTherapyCount', currentState.generalPaStepTherapy, { shouldValidate: true, shouldDirty: true });
                  }
                } else if (!hasGeneralPdl && hasGeneralNonPdl) {
                  // Set step therapy value for General Non-PDL only
                  if (currentState.generalNonPdlStepTherapy) {
                    form.setValue('stepTherapyCount', currentState.generalNonPdlStepTherapy, { shouldValidate: true, shouldDirty: true });
                  }
                } else if (hasGeneralPdl && hasGeneralNonPdl) {
                  // Clear step therapy value when both are selected
                  form.setValue('stepTherapyCount', '', { shouldValidate: true, shouldDirty: true });
                }
              }
            }
          }
        };

        return (
          <VerticalFormField
            htmlFor={`${formId}-${name}`}
            label="PA Types"
            actions={visibleFields && onFieldShow && onFieldHide ? (
              <FieldActions
                name={name}
                visibleFields={visibleFields}
                onFieldShow={onFieldShow}
                onFieldHide={onFieldHide}
              />
            ) : null}
            name={name}
            visibleFields={visibleFields}
            error={error?.message}
            nonEditableText={fieldDisplayMode === 'show-default-value'
              ? <PaTypeCell format="pa-type" type="text" paTypes={value}/>
              : 'No Change'
            }
          >
            <SelectMultiple
              name={name}
              id={`${formId}-${name}`}
              placeholder="Select PA Types"
              options={paTypes ?? []}
              value={value}
              labelKey="label"
              valueKey="id"
              onBlur={onBlur}
              onChange={handlePaTypesChange}
              ref={ref}
            />
          </VerticalFormField>
        );
      }}
    />
  );
};

// Helper component to handle the State field
const StateField = ({ 
  formId, 
  form, 
  visibleFields, 
  onFieldShow, 
  onFieldHide, 
  fieldDisplayMode, 
  stateOptions,
  statesAsync
}: { 
  formId: string; 
  form: any;
  visibleFields?: FieldName[];
  onFieldShow?: (fieldName: FieldName) => void;
  onFieldHide?: (fieldName: FieldName) => void;
  fieldDisplayMode?: 'show-default-value' | 'show-no-change';
  selectedState?: State | null;
  stateOptions: any[];
  statesAsync: any;
}) => {
  return (
    <Controller
      name="state"
      control={form.control}
      rules={{
        required: !visibleFields || visibleFields.includes('state') ? 'State is a required field' : false
      }}
      render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => {
        // Handle state changes with inline controller logic
        const handleStateChange = (newValue: Lookup) => {
          onChange(newValue);
          
          // Store states data in form for other components to access
          if (statesAsync.value) {
            // Store states data in the form for other components to access
            (form as any)._statesData = statesAsync.value;
            
            // Inline implementation of handleStateChange logic
            const statesData = statesAsync.value;
            
            if (statesData) {
              // Check if the "same as PDL link" checkbox is checked
              const isChecked = form.getValues('isStatePaCriteriaSameAsPdlLink');
              
              if (newValue?.id && isChecked) {
                // If checked and we have a state selected, update the PA criteria URL
                const selectedStateObj = statesData.find((state: { id: number; }) => state.id === newValue.id);
                if (selectedStateObj?.pdlLink) {
                  form.setValue('paCriteriaSourceUrl', selectedStateObj.pdlLink, { shouldValidate: true, shouldDirty: true });
                }
              }
              
              // When state changes, we should also update PA criteria based on PA Types selection
              const selectedPaTypes = form.getValues('paTypes') || [];
              if (selectedPaTypes.length > 0 && newValue?.id) {
                const currentState = statesData.find((state: { id: number; }) => state.id === newValue.id);
                
                if (currentState) {
                  // Check if General PDL or General Non-PDL is selected
                  const hasGeneralPdl = selectedPaTypes.some((type: { id: number; }) => type.id === 1); // General PDL
                  const hasGeneralNonPdl = selectedPaTypes.some((type: { id: number; }) => type.id === 2); // General Non-PDL
                  
                  // Update PA Types related content based on the new state
                  if (hasGeneralPdl || hasGeneralNonPdl) {
                    // Update PA Criteria text as needed
                    let currentText = form.getValues('paCriteriaDescription') || '';
                    let shouldUpdateText = false;
                    
                    // Handle PA forms link updates
                    if (currentState.paFormsLink && (hasGeneralPdl || hasGeneralNonPdl)) {
                      form.setValue('paCriteriaSourceUrl', currentState.paFormsLink, { shouldValidate: true, shouldDirty: true });
                    }
                    
                    // Update step therapy based on the PA types
                    if (hasGeneralPdl && !hasGeneralNonPdl && currentState.generalPaStepTherapy) {
                      form.setValue('stepTherapyCount', currentState.generalPaStepTherapy, { shouldValidate: true, shouldDirty: true });
                    } else if (!hasGeneralPdl && hasGeneralNonPdl && currentState.generalNonPdlStepTherapy) {
                      form.setValue('stepTherapyCount', currentState.generalNonPdlStepTherapy, { shouldValidate: true, shouldDirty: true });
                    }
                    
                    // Add PDL text if needed
                    if (hasGeneralPdl && currentState.generalPaCriteriaPdlReviewed) {
                      const pdlText = currentState.generalPaCriteriaPdlReviewed;
                      if (!currentText.includes(pdlText)) {
                        currentText = currentText ? `${currentText}\n\n${pdlText}` : pdlText;
                        shouldUpdateText = true;
                      }
                    }
                    
                    // Add Non-PDL text if needed
                    if (hasGeneralNonPdl && currentState.generalPaCriteriaPdlNonReviewed) {
                      const nonPdlText = currentState.generalPaCriteriaPdlNonReviewed;
                      if (!currentText.includes(nonPdlText)) {
                        currentText = currentText ? `${currentText}\n\n${nonPdlText}` : nonPdlText;
                        shouldUpdateText = true;
                      }
                    }
                    
                    // Only update if text has changed
                    if (shouldUpdateText) {
                      form.setValue('paCriteriaDescription', currentText, { shouldValidate: true, shouldDirty: true });
                    }
                  }
                }
              }
            }
          }
        };
        
        return (
          <VerticalFormField
            htmlFor={`${formId}-${name}`}
            label="State"
            actions={visibleFields && onFieldShow && onFieldHide ? (
              <FieldActions
                name={name}
                visibleFields={visibleFields}
                onFieldShow={onFieldShow}
                onFieldHide={onFieldHide}
              />
            ) : null}
            name={name}
            visibleFields={visibleFields}
            error={error?.message}
            nonEditableText={fieldDisplayMode === 'show-default-value' ? value?.label : 'No Change'}
            alreadyHasFormField
          >
            {!visibleFields || visibleFields.includes(name) ? (
              <InMemorySelectList
                value={value}
                name={name}
                onChange={handleStateChange}
                onBlur={onBlur}
                ref={ref}
                placeholder="Select a State"
                options={stateOptions as Lookup[]}
              />
            ) : (
              <div>No Change</div>
            )}
          </VerticalFormField>
        );
      }}
    />
  );
};
